import { useEffect, useState } from 'react';
import { RightOutlined } from '@ant-design/icons';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import i18n, { type SupportedLanguage } from '@/locales';
import { ContactSection } from '@/pages/Home/components/HomeBusiness';
import laptop from '@/assets/imgs/travel/laptop.png';
import logoMarquee from '@/assets/imgs/travel/logo-marquee.png';
import bannerOverlay from '@/assets/imgs/travel/banner-overlay.png';
import introBackground from '@/assets/imgs/travel/intro-bg.png';
import advantagesImage from '@/assets/imgs/travel/advantages.png';
import integratedIcon from '@/assets/imgs/travel/advantage-integrated.svg';
import operationIcon from '@/assets/imgs/travel/advantage-operation.svg';
import globalIcon from '@/assets/imgs/travel/advantage-global.svg';
import languageIcon from '@/assets/imgs/travel/advantage-language.svg';
import serviceMask from '@/assets/imgs/travel/service-mask.svg';
import servicePanel from '@/assets/imgs/travel/service-panel.svg';
import serviceArrow from '@/assets/imgs/travel/service-arrow.svg';
import servicePageNext from '@/assets/imgs/travel/service-page-next.svg';
import serviceAirport from '@/assets/imgs/travel/service-airport.png';
import serviceVip from '@/assets/imgs/travel/service-vip.png';
import serviceCoupon from '@/assets/imgs/travel/service-coupon.png';
import serviceLuggage from '@/assets/imgs/travel/service-luggage.png';
import serviceTax from '@/assets/imgs/travel/service-tax.png';
import serviceTransport from '@/assets/imgs/travel/service-transport.png';
import serviceTicket from '@/assets/imgs/travel/service-ticket.png';
import serviceHotel from '@/assets/imgs/travel/service-hotel.png';

const services = [
  { image: serviceAirport, key: 'airportShopping' },
  { image: serviceVip, key: 'vip' },
  { image: serviceCoupon, key: 'coupon' },
  { image: serviceLuggage, key: 'luggage' },
  { image: serviceTax, key: 'taxRefund' },
  { image: serviceTransport, key: 'transport' },
  { image: serviceTicket, key: 'tickets' },
  { image: serviceHotel, key: 'hotel' },
] as const;

const SERVICE_CARD_WIDTH = 420;
const SERVICE_CARD_HEIGHT = 544.5;
const SERVICE_CARD_STEP = 470;
const SERVICE_FOCUSED_WIDTH = 504;
const SERVICE_FOCUSED_SCALE = SERVICE_FOCUSED_WIDTH / SERVICE_CARD_WIDTH;
const SERVICE_TRACK_LEFT = 60;
const SERVICE_TRACK_RIGHT = 60;
const toRem = (value: number) => `${value / 16}rem`;
const bilionpassUrl = (language: SupportedLanguage) =>
  `https://www.bilionpass.com?lang=${encodeURIComponent(language)}`;
const servicePaths: Record<(typeof services)[number]['key'], string> = {
  airportShopping: '/duty-free-pre-order',
  vip: '/vip',
  coupon: '/coupon',
  luggage: '/airport-luggage-delivery',
  taxRefund: '/',
  transport: '/',
  tickets: '/',
  hotel: '/',
};
const bilionpassServiceUrl = (key: (typeof services)[number]['key'], language: SupportedLanguage) =>
  `https://www.bilionpass.com${servicePaths[key]}?lang=${encodeURIComponent(language)}`;

const getDesignViewportWidth = () => {
  if (typeof window === 'undefined') return 1920;
  const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return document.documentElement.clientWidth / (rootFontSize / 16);
};

type ServiceCardProps = {
  active: boolean;
  description: string;
  image: string;
  title: string;
  x: number;
  href: string;
};

function ServiceCard({ active, description, image, title, x, href }: ServiceCardProps) {
  const reduceMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const hoverTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.3, ease: [0.4, 0, 0.2, 1] as const };

  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={title}
      className="absolute left-0 top-[54.75px] h-[544.5px] w-[420px] origin-left will-change-transform"
      animate={{ x: toRem(x), scale: active ? SERVICE_FOCUSED_SCALE : 1 }}
      initial={false}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{ transformOrigin: '0% 50%', zIndex: active ? 2 : 1 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.5, ease: [0.65, 0, 0.35, 1] }}
    >
      <div
        className="absolute left-0 top-0 overflow-hidden rounded-[16px]"
        style={{
          width: toRem(SERVICE_CARD_WIDTH),
          height: toRem(530),
          WebkitMaskImage: `url(${serviceMask})`,
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskSize: '100% 100%',
          maskImage: `url(${serviceMask})`,
          maskRepeat: 'no-repeat',
          maskSize: '100% 100%',
        }}
      >
        <motion.img
          src={image}
          alt=""
          className="object-cover size-full will-change-transform"
          animate={{ scale: hovered && !reduceMotion ? 1.1 : 1 }}
          initial={false}
          transition={hoverTransition}
        />
        <div className="absolute bottom-[12.5px] left-[10px] right-[10px] h-[160px]">
          <img src={servicePanel} alt="" className="size-full" />
        </div>

        <div
          className="absolute text-white"
          style={{ left: toRem(40), right: toRem(92), top: toRem(SERVICE_CARD_HEIGHT * 0.7006) }}
        >
          <h3 className="whitespace-nowrap text-[32px] font-semibold leading-none">{title}</h3>
          <p className="w-[250px] mt-[16px] text-[20px] leading-[1.3] text-white/60 line-clamp-3 ">{description}</p>
        </div>
      </div>

      <span
        className="absolute flex items-center justify-center rounded-full border-white bg-[#00a0e9]"
        style={{
          width: toRem(63),
          height: toRem(63),
          left: toRem(SERVICE_CARD_WIDTH * 0.731),
          top: toRem(481.5),
          borderWidth: toRem(1.5),
        }}
      >
        <motion.img
          src={serviceArrow}
          alt=""
          className="h-[23px] w-[31px] will-change-transform"
          animate={{ rotate: hovered && !reduceMotion ? -45 : 0 }}
          initial={false}
          transition={hoverTransition}
        />
      </span>
    </motion.a>
  );
}

const advantages = [
  { icon: integratedIcon, key: 'integrated' },
  { icon: globalIcon, key: 'global' },
  { icon: operationIcon, key: 'operation' },
  { icon: languageIcon, key: 'language' },
] as const;

const Canvas = ({ children }: { children: React.ReactNode }) => (
  <div className="relative left-1/2 h-full w-[1920px] -translate-x-1/2">{children}</div>
);

const LogoMarquee = () => (
  <img src={logoMarquee} alt="" aria-hidden="true" className="block w-full h-auto shrink-0" />
);

const Travel = () => {
  const { t } = useTranslation();
  const language = (i18n.resolvedLanguage ?? i18n.language) as SupportedLanguage;
  const introDescriptionSize = i18n.resolvedLanguage === 'zh-CN' ? 'text-[34px]' : 'text-[28px]';
  const introDescriptionLeading = i18n.resolvedLanguage === 'zh-CN' ? 'leading-[1.6]' : 'leading-[1.5]';
  const advantageTitleSize = i18n.resolvedLanguage === 'en-US' ? 'text-[32px]' : 'text-[40px]';
  const advantageDescriptionSize = i18n.resolvedLanguage === 'en-US' ? 'text-[22px]' : 'text-[28px]';
  const [focusedService, setFocusedService] = useState(0);
  const [serviceViewportWidth, setServiceViewportWidth] = useState(getDesignViewportWidth);

  useEffect(() => {
    const updateViewportWidth = () => setServiceViewportWidth(getDesignViewportWidth());
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  const getServiceX = (index: number) => {
    // Keep the first two states on the same left rail, center the middle
    // states, then keep the final two states on the same right rail. This
    // makes the penultimate state mirror the second state: the last normal
    // card is already flush right before it grows.
    const focusedNaturalX = SERVICE_TRACK_LEFT + focusedService * SERVICE_CARD_STEP;
    const lastNaturalX = SERVICE_TRACK_LEFT + (services.length - 1) * SERVICE_CARD_STEP;
    const rightAlignedTrackOffset =
      lastNaturalX - (serviceViewportWidth - SERVICE_TRACK_RIGHT - SERVICE_FOCUSED_WIDTH);
    const trackOffset =
      focusedService < 2
        ? 0
        : focusedService >= services.length - 2
        ? rightAlignedTrackOffset
        : focusedNaturalX - (serviceViewportWidth - SERVICE_FOCUSED_WIDTH) / 2;
    const baseX = SERVICE_TRACK_LEFT + index * SERVICE_CARD_STEP - trackOffset;

    // Preserve the designed 50px gap after the 84px-wider focused card without
    // treating that local reflow as movement of the carousel rail.
    return index > focusedService ? baseX + SERVICE_FOCUSED_WIDTH - SERVICE_CARD_WIDTH : baseX;
  };

  return (
    <div className="min-w-[1480px] overflow-hidden bg-white">
      <section className="relative h-[500px] bg-white">
        <div className="mx-auto mt-[50px] grid h-[500px] w-[1800px] overflow-hidden rounded-[30px]">
          <div className="col-start-1 row-start-1">
            <img
              src={bannerOverlay}
              alt=""
              className="block object-cover object-bottom size-full"
            />
          </div>
          <div className="flex flex-col items-center justify-center col-start-1 row-start-1 text-center text-white">
            <h1 className="font-douyin text-[66px] font-bold leading-[1.5]">
              One Platform. Unlimited Travel Possibilities.
            </h1>
            <p className="mt-[-2px] text-[50px] font-medium leading-[1.5]">
              {t('travel.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <section className="overflow-hidden bg-white">
        <div
          className="mx-auto mt-[50px] flex h-[870px] w-[1700px] rounded-[40px] bg-cover bg-bottom bg-no-repeat"
          style={{ backgroundImage: `url(${introBackground})`, backgroundSize: '110% 110%' }}
        >
          <div className="flex items-start pl-[113px] pt-[98px]">
            <div className="w-[630px] shrink-0 text-[#355f72]">
              <h2 className="w-[1300px] whitespace-pre-line text-[70px] font-medium leading-[1.5]">
                {t('travel.intro.title')}
              </h2>
              <p className={`mt-[16px] w-[630px] ${introDescriptionSize} ${introDescriptionLeading}`}>
                {t('travel.intro.description')}
              </p>
              <a
                href={bilionpassUrl(language)}
                target="_blank"
                rel="noreferrer"
                className="group mt-[32px] inline-flex h-[80px] min-w-[320px] items-center justify-center overflow-hidden rounded-full border-2 border-[#00a0e9] bg-[#00a0e9] px-[40px] text-[37px] font-medium text-white transition-[background-color,color] duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-white hover:text-[#00a0e9] motion-reduce:duration-0"
              >
                <span className="flex items-center gap-[14px] whitespace-nowrap transition-transform duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-[21px] motion-reduce:duration-0">
                  View More
                  <RightOutlined
                    aria-hidden="true"
                    className="text-[28px] opacity-100 transition-opacity duration-[120ms] group-hover:opacity-0 motion-reduce:duration-0"
                  />
                </span>
              </a>
            </div>
            <img
              src={laptop}
              alt={t('travel.images.platform')}
              className="-ml-[129px] mt-[122px] h-[492px] w-[1176px] shrink-0 object-contain"
            />
          </div>
        </div>
        <div className="mb-[68px] mt-[52px]">
          <LogoMarquee />
        </div>
      </section>

      <section className="h-[1212px] overflow-hidden rounded-t-[80px] bg-[#169af3] text-white">
        <h2 className="pt-[108px] text-center font-douyin text-[70px] font-bold leading-none">
          {t('travel.advantages.title')}
        </h2>
        <div className="mx-auto mt-[67px] flex w-[1400px] items-start gap-[48px]">
          <img
            src={advantagesImage}
            alt={t('travel.images.destination')}
            className="mt-[31px] h-[684px] w-[652px] shrink-0 object-contain"
          />
          <div className="grid w-[700px] grid-cols-2 gap-x-[60px] gap-y-[47px]">
            {advantages.map(item => (
              <article key={item.key} className="h-[345px] w-[340px]">
                <img src={item.icon} alt="" className="size-[120px]" />
                <h3 className={`mt-[2px] whitespace-nowrap ${advantageTitleSize} font-semibold leading-[1.6]`}>
                  {t(`travel.advantages.items.${item.key}.title`)}
                </h3>
                <p className={`mt-[-2px] w-[330px] ${advantageDescriptionSize} leading-[1.5]`}>
                  {t(`travel.advantages.items.${item.key}.description`)}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="travel-services" className="relative z-[1] -mt-[100px] h-[990px] isolate overflow-hidden rounded-t-[80px] bg-gradient-to-b from-[#cdeaff] via-white to-white">
        <Canvas>
          <h2 className="absolute inset-x-0 top-[91px] text-center font-douyin text-[70px] font-bold leading-none text-[#355f72]">{t('travel.services.title')}</h2>
          <div
            className="absolute left-1/2 top-[252px] h-[730px] -translate-x-1/2 overflow-hidden"
            style={{ width: toRem(serviceViewportWidth) }}
          >
            {services.map((service, index) => (
              <ServiceCard
                key={service.key}
                image={service.image}
                title={t(`travel.services.items.${service.key}.title`)}
                description={t(`travel.services.items.${service.key}.description`)}
                active={index === focusedService}
                x={getServiceX(index)}
                href={bilionpassServiceUrl(service.key, language)}
              />
            ))}
          </div>
          <div className="absolute left-[838px] top-[954px] flex h-[27px] items-center gap-[68.5px]">
            <button
              type="button"
              onClick={() => setFocusedService((current) => Math.max(0, current - 1))}
              disabled={focusedService === 0}
              aria-label={t('travel.services.previous')}
              className="h-[27px] w-[87.5px] border-0 bg-transparent p-0 disabled:opacity-35"
            >
              <img src={servicePageNext} alt="" className="rotate-180 size-full" />
            </button>
            <button
              type="button"
              onClick={() => setFocusedService((current) => Math.min(services.length - 1, current + 1))}
              disabled={focusedService === services.length - 1}
              aria-label={t('travel.services.next')}
              className="h-[27px] w-[87.5px] border-0 bg-transparent p-0 disabled:opacity-35"
            >
              <img src={servicePageNext} alt="" className="size-full" />
            </button>
          </div>
        </Canvas>
      </section>
      <div className="mb-[36px] mt-[48px]">
        <LogoMarquee />
      </div>
      <ContactSection overlapPrevious={false} />
    </div>
  );
};

export default Travel;
